// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { UserId } from '@evolvemep/api-ts';
import { Loader } from '@mantine/core';
import { isAfter, parseISO } from 'date-fns';
// oxlint-disable-next-line no-restricted-imports
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { isNotNil } from 'helpers/isNotNil';

import { isInvitationType, useConfirmUser, useGetAuthenticationUrl } from './auth/restCalls';
import type { LoginErrorCode } from './components/LoginErrorAlert';
import { Join } from './Join/Join';
import { buildSsoStateVar } from './Login/helper';

export const AcceptInvitation = () => {
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const { confirmUser, confirming } = useConfirmUser();
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<LoginErrorCode>();
  const { getAuthenticationUrl } = useGetAuthenticationUrl();
  // All these come from a link in the invite email
  const [queryParams] = useSearchParams();
  const userId = queryParams.get('userId') as UserId | null;
  const id = queryParams.get('id');
  const inviteSourceName = queryParams.get('name');
  const type = queryParams.get('type');
  const idp = queryParams.get('idp');
  const code = queryParams.get('code');
  const expirationDateStr = queryParams.get('expiration');

  useEffect(() => {
    if (expirationDateStr) {
      const expirationDate = parseISO(expirationDateStr);
      const hasExpired = isAfter(new Date(), expirationDate);
      if (hasExpired) {
        setLoading(false);
        setErrorCode('InvitationExpired');
        return;
      }
    }

    const checkUserAuthenticationUrl = async () => {
      if (isNotNil(userId)) {
        await getAuthenticationUrl(`user/${userId}`)
          .then((urlResponse) => {
            const destinationURL = urlResponse?.returnUrl;
            if (destinationURL?.startsWith('https://')) {
              window.location.href = `${destinationURL}&state=${buildSsoStateVar({ confirmationCode: code ?? undefined, userId })}`;
            }
          })
          .catch((err: unknown) => {
            if (err === 'Not Found') {
              setErrorCode('UserNotFoundException');
            } else {
              setErrorCode('UnknownException');
            }
          });
      }
    };

    (async () => {
      try {
        if (isNotNil(id)) {
          if (isInvitationType(type) && isNotNil(code)) {
            // If `idp` is not nil and is not empty
            if (idp) {
              await checkUserAuthenticationUrl();
              return;
            }
            await confirmUser(id, type, {
              confirmationCode: code,
            })
              .then(() => {
                void navigate('/auth/login', {
                  state: {
                    joinConfirm: inviteSourceName,
                  },
                });
              })
              .catch((e: unknown) => {
                // oxlint-disable-next-line no-console
                console.error('Could not accept invite directly, user is likely not confirmed.', e);
              })
              .finally(checkUserAuthenticationUrl);
          }
        }
      } catch (
        // oxlint-disable-next-line typescript/no-explicit-any
        error: any
      ) {
        setErrorCode(error ?? 'UnknownException');
      } finally {
        setLoading(false);
      }
    })();
  }, [code, confirmUser, expirationDateStr, userId, idp, inviteSourceName, navigate, type, getAuthenticationUrl, id]);

  if (errorCode === 'InvitationExpired') {
    return <Navigate to="/invitation-expired" />;
  }

  if (errorCode || !code || !id || !userId || !userId || !isInvitationType(type)) {
    return <Navigate to={`/auth/login?errorCode=${errorCode ?? 'UnknownException'}`} />;
  }

  if (loading || confirming) return <Loader />;

  return <Join inviteSourceName={inviteSourceName} type={type} id={id} code={code} idp={idp} />;
};
